// Graphic_Designing_In_Noida.tsx

import React, { useState } from 'react';
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

// Import icons for the 'Why Choose Us' section (similar to Digital Marketing page)
import { FaLaptopCode, FaCertificate, FaBriefcase, FaFileAlt, FaComments, FaUsers } from 'react-icons/fa';

import JoinNowFormPopup from "../../componants/JoinNowFormPopup"; // Assuming this path is correct

// Placeholder imports for graphic design-related images/logos
// You'll need to replace these with actual paths to your images
// import graphicDesignHeroImg from "../../assets/Internship/graphic_design_hero.png"; // A new hero image for graphic design
import photoshopLogo from "../../assets/Internship/Photoshop.webp"; // Example: path to Photoshop logo
import illustratorLogo from "../../assets/Internship/Illustrator.jpg"; // Example: path to Illustrator logo
import indesignLogo from "../../assets/Internship/indesign.png"; // Example: path to InDesign logo
import figmaLogo from "../../assets/Internship/figma.jpg"; // Example: path to Figma logo
import corelDrawLogo from "../../assets/Internship/coreldraw.webp"; // Example: path to CorelDRAW logo
import canvaLogo from "../../assets/Internship/canva.webp"; // Example: path to Canva logo

import processImg from "../../assets/Internship/our-process.webp"; // Using the same process image for now

// Animation variants (reusing from Digital Marketing page)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const GraphicDesigningInNoida: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState("");

  const handleOpenPopup = (courseTitle: string) => {
    setSelectedCourseTitle(courseTitle);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedCourseTitle("");
  };

  const whyChooseUsBenefits = [
    {
      icon: <FaLaptopCode className="text-orange-400 text-5xl mb-4" />,
      title: "Hybrid Mode Class",
      description: "Learn flexibly with combined online and offline classroom training sessions.",
    },
    {
      icon: <FaCertificate className="text-orange-400 text-5xl mb-4" />,
      title: "Internship Certificate",
      description: "Receive an internship certificate for hands-on industry project experience.",
    },
    {
      icon: <FaBriefcase className="text-orange-400 text-5xl mb-4" />,
      title: "100% Placement Assistance",
      description: "Complete placement support provided until you secure a suitable job.",
    },
    {
      icon: <FaFileAlt className="text-orange-400 text-5xl mb-4" />,
      title: "CV Building & ATS Score",
      description: "Optimize your resume for ATS and improve shortlisting chances significantly.",
    },
    {
      icon: <FaComments className="text-orange-400 text-5xl mb-4" />,
      title: "Mock Interviews",
      description: "Practice with mock interviews to boost real interview performance confidence.",
    },
    {
      icon: <FaUsers className="text-orange-400 text-5xl mb-4" />,
      title: "15 Students Per Batch",
      description: "Small batches ensure personalized attention and better student-teacher interaction.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans antialiased">
      <Helmet>
        <title>Graphic Designing Training in Noida | Appwars Technologies</title>
        <meta name="description" content="Master graphic design with comprehensive industrial training in Noida. Learn Adobe Photoshop, Illustrator, InDesign, UI/UX, and more. Build a stunning portfolio with 100% placement and hybrid classes by Appwars Technologies." />
        <meta name="keywords" content="graphic design, Adobe Photoshop, Adobe Illustrator, Adobe InDesign, Figma, UI/UX, branding, visual communication, Noida, training, placement, internship, Appwars Technologies, Nadezhda Ventures" />
        <meta name="author" content="Appwars Technologies / Nadezhda Ventures" />
        <meta property="og:title" content="Graphic Designing Training in Noida | Appwars Technologies" />
        <meta property="og:description" content="Unleash your creativity with comprehensive Graphic Designing training programs in Noida." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://yourwebsite.com/graphic-design-noida" /> {/* Replace with your actual website URL */}
        <link rel="canonical" href="http://yourwebsite.com/graphic-design-noida" /> {/* Replace with your actual canonical URL */}
      </Helmet>

      <main>
        {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
        <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
          {/* Transparent Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://t3.ftcdn.net/jpg/07/89/84/68/360_F_789846803_lTbXp4wIQg04Kf3IQJH5fQa1g1h6Fk4U.jpg" // Using a generic technology background image
              alt="Graphic Design Transparent Background"
              className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
            />
            {/* Optional: Add a subtle overlay for better text readability and darker background */}
            <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
          </div>

          {/* Text and Image Content */}
          <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
            <motion.div
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold text-orange-500 mb-6">
                Graphic Designing Training
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Unleash your creativity and master the art of visual communication with Nadezhda Venture's hands-on training program.
              </p>

              <button
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                onClick={() => handleOpenPopup("Graphic Designing Training")}
              >
                Join Now !
              </button>
            </motion.div>

            {/* <motion.div
              className="flex-1 w-full max-w-2xl flex justify-center items-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <img
                src={graphicDesignHeroImg} // Placeholder for a graphic design-specific hero image
                alt="Graphic Designing Showcase"
                className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]"
              />
            </motion.div> */}
          </div>
        </section>


        {/* Key Graphic Design Tools & Concepts Section */}
        <section className="px-6 md:px-20 py-24 bg-gray-300 text-orange-600">
          <h2 className="text-5xl font-bold text-center mb-10">Key Graphic Design Tools & Concepts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {/* Adobe Photoshop */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={photoshopLogo} alt="Photoshop Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Adobe Photoshop</h3>
              <button
                onClick={() => handleOpenPopup("Adobe Photoshop Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Adobe Illustrator */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={illustratorLogo} alt="Illustrator Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Adobe Illustrator</h3>
              <button
                onClick={() => handleOpenPopup("Adobe Illustrator Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Adobe InDesign */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={indesignLogo} alt="InDesign Icon" className="w-18 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Adobe InDesign</h3>
              <button
                onClick={() => handleOpenPopup("Adobe InDesign Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Figma */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={figmaLogo} alt="Figma Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Figma</h3>
              <button
                onClick={() => handleOpenPopup("Figma for UI/UX")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* CorelDRAW */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={corelDrawLogo} alt="CorelDRAW Icon" className="w-20 h-20 mb-2"/>
              <h3 className="text-lg font-semibold text-center">CorelDRAW</h3>
              <button
                onClick={() => handleOpenPopup("CorelDRAW Training")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

            {/* Canva */}
            <div className="flex flex-col items-center p-4 bg-white rounded-xl shadow-lg hover:scale-105 transition-transform">
              <img src={canvaLogo} alt="Canva Icon" className="w-16 h-16 mb-2"/>
              <h3 className="text-lg font-semibold text-center">Canva</h3>
              <button
                onClick={() => handleOpenPopup("Canva for Graphic Design")}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-4 rounded-xl transition-all duration-300">Join Now</button>
            </div>

          </div>
        </section>

        {/* Why Choose Us Section (from image_ab7e7d.png, styling adapted) */}
        <section className="px-6 md:px-20 py-16 md:py-24 bg-white text-orange-600">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose Us for Graphic Designing?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUsBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 bg-blue-950 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                {benefit.icon}
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>


        {/* Our Training & Placement Process Section */}
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-orange-500">Our Training & Placement Process</h2>
        <img
          src={processImg} // Path to your process image file
          alt="Our Training and Placement Process"
          className="w-full h-auto object-cover"
        />

      </main>

      {/* Floating Contact Us button */}
      {/* <a
        href="#"
        className="fixed bottom-8 right-8 bg-green-500 text-white text-lg font-semibold py-3 px-6 rounded-full shadow-xl hover:bg-green-600 transition-colors duration-300 z-50 transform hover:scale-105"
        onClick={(e) => {
          e.preventDefault(); // Prevent default link behavior
          handleOpenPopup("General Graphic Design Inquiry");
        }}
      >
        Contact Us
      </a> */}

      {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default GraphicDesigningInNoida;